// ══════════════════════════════════════════════════════════════════════════════════════════════
//  «CAMBIAR DE CLÍNICA» EN EL MENÚ DEL PERFIL   (t93, 5-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño: «Poder cambiar entre clínicas cuando un mismo correo tiene varias clínicas desde el botón de mi perfil».
//  Las clínicas salen de la sesión (`agente.otras_clinicas`, que da el servidor al entrar: SOLO las que la contraseña —o
//  la llave de Face ID— abrió, las mismas tarjetas del «¿en qué clínica entras?»). Al elegir una, el servidor abre una
//  sesión NUEVA de esa clínica y cierra la de ahora (backend/services/cambioDeClinica.js), y la pantalla entra con ella
//  como en un inicio de sesión: borra todo lo de la sesión anterior y vuelve a arrancar la aplicación entera
//  (components/Layout · cambiarDeClinica), así que no queda en pantalla ni en memoria nada de la clínica de antes.
//
//  SIN ESTADO DE REACT (un <details>, como el «?» de utils/contactoBiango) y sin más imports: el menú lo pinta el Layout,
//  que montan cientos de guardias, y un `utils/` así lo cargan de verdad sin tener que tocar ninguna.

/** Las clínicas a las que se puede cambiar, sacadas de la sesión (nunca la de ahora). */
import { t } from './i18n.js'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export function clinicasParaCambiar(usuario) {
  const lista = usuario && Array.isArray(usuario.otras_clinicas) ? usuario.otras_clinicas : []
  const ahora = Number(usuario && usuario.clinica_id)
  return lista.filter((c) => c && Number.isInteger(Number(c.clinica_id)) && Number(c.clinica_id) > 0
    && Number(c.clinica_id) !== ahora && String(c.nombre || '').trim())
}

/** Pide al servidor la sesión de la otra clínica: la misma respuesta que el login (token + agente) o un `segundo_factor`. */
export async function pedirCambioDeClinica(api, clinicaId) {
  const { data } = await api.post('/auth/cambiar-clinica', { clinica_id: clinicaId })
  return data || {}
}

/** El porqué, tal y como lo dice el servidor. */
export function errorDelCambio(e) {
  return (e && e.response && e.response.data && e.response.data.error) || t('utils.cambioDeClinica.no_se_ha_podido_cambiar', 'No se ha podido cambiar de clínica. Inténtalo de nuevo.')
}

/** El `sid` (el dispositivo) de un token, sin verificar nada: solo para pedir que se cierre esa sesión. */
export function sidDelToken(token) {
  try {
    const p = String(token || '').split('.')[1] || ''
    return JSON.parse(atob(p.replace(/-/g, '+').replace(/_/g, '/'))).sid || null
  } catch (_) { return null }
}

/** Lo de la clínica anterior que este navegador guarda FUERA de la sesión (el formato regional), para no pintarlo ni un instante. */
export function olvidarLoDeLaAnterior() {
  try { localStorage.removeItem('regional_cache:' + (typeof location !== 'undefined' ? location.host : 'app')) } catch (_) { /* sin almacenamiento */ }
}

const inicial = (n) => String(n || '?').trim().charAt(0).toUpperCase() || '?'

/**
 * El desplegable «Cambiar de clínica». `oscuro`: en la barra lateral (fondo oscuro). `ocupada`: la clínica a la que se
 * está cambiando (los botones se apagan mientras tanto). Sin otras clínicas no pinta nada.
 */
export function CambioDeClinica({ clinicas, onElegir, oscuro = false, ocupada = null }) {
  const lista = Array.isArray(clinicas) ? clinicas : []
  if (!lista.length) return null
  const tinta = oscuro ? 'rgba(255,255,255,0.78)' : 'var(--text-primary)'
  const suave = oscuro ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)'
  return (
    <details className="cambio-clinica" data-cambio-clinica={oscuro ? 'barra' : 'menu'}
      style={{ margin: oscuro ? '0 0 8px' : '2px 0', borderRadius: '8px', background: oscuro ? 'rgba(255,255,255,0.04)' : 'transparent' }}>
      <summary style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: oscuro ? '8px 10px' : '9px 12px', borderRadius: '8px',
        fontSize: oscuro ? '12px' : '13px', fontWeight: 500, color: tinta, cursor: 'pointer', listStyle: 'none' }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M7 4 3 8l4 4" /><path d="M3 8h13" /><path d="m17 20 4-4-4-4" /><path d="M21 16H8" />
        </svg>
        <span style={{ flex: 1 }}>Cambiar de clínica</span>
        <span style={{ fontSize: '11px', color: suave }}>{lista.length}</span>
      </summary>
      <div role="list" aria-label={t('utils.cambioDeClinica.tus_otras_clinicas', 'Tus otras clínicas')} style={{ display: 'flex', flexDirection: 'column', gap: '2px', padding: oscuro ? '0 6px 8px' : '0 4px 6px' }}>
        {lista.map((c) => {
          const esta = ocupada != null && Number(ocupada) === Number(c.clinica_id)
          return (
            <div role="listitem" key={c.clinica_id}>
              <button type="button" data-clinica-cambio={c.clinica_id} disabled={ocupada != null}
                onClick={() => { if (typeof onElegir === 'function') onElegir(c) }}
                title={t('utils.cambioDeClinica.entrar_en', 'Entrar en {{nombre}}', { nombre: c.nombre })}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '9px', padding: '7px 8px', minHeight: '36px', borderRadius: '8px',
                  border: 'none', background: 'transparent', color: tinta, fontSize: '12.5px', textAlign: 'left',
                  cursor: ocupada != null ? 'default' : 'pointer', opacity: ocupada != null && !esta ? 0.55 : 1 }}>
                <span aria-hidden="true" style={{ width: '22px', height: '22px', borderRadius: '6px', flexShrink: 0, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '11px', fontWeight: 800, color: c.color ? '#fff' : 'var(--sobre-primary)', background: c.color || 'var(--primary)' }}>{inicial(c.nombre)}</span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nombre}</span>
                {esta && <span style={{ fontSize: '11px', color: suave }}>Entrando…</span>}
              </button>
            </div>
          )
        })}
      </div>
    </details>
  )
}
